import React, { useState, useEffect, useContext, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Alert, AlertDescription } from '../../ui/alert';
import { useToast } from '../../ui/use-toast';
import { 
  Clock, 
  Play, 
  Square, 
  Coffee, 
  Package,
  MapPin,
  Briefcase,
  Timer,
  TrendingUp,
  Calendar,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { User, Project, Location, WorkEntry } from '../../../types';
import { apiService, ApiWorkEntry, ApiUser } from '../../../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { MaterialConsumptionForm } from '../../materials/MaterialConsumptionForm';
import { MaterialConsumptionList } from '../../materials/MaterialConsumptionList';
import { RealTimeContext } from '../../../contexts/RealTimeContext';

interface PersonalTrackingTabProps {
  user: User;
  projects: Project[];
  locations: Location[];
}

interface WorkStats {
  todayHours: number;
  weekHours: number;
  monthHours: number;
  activeSessions: number;
}

type PersonalOperation = {
  operation: 'start' | 'stop';
  timestamp: number;
  projectId?: number;
  locationId?: number;
};

// Global storage for personal tracking operations that persists across tab switches
const globalPersonalOperations = new Map<number, PersonalOperation>();

// Function to clean up old operations from global storage
const cleanupPersonalOperations = () => {
  const now = Date.now();
  for (const [userId, op] of globalPersonalOperations.entries()) {
    if (now - op.timestamp > 90000) { // Remove operations older than 90 seconds
      globalPersonalOperations.delete(userId);
    }
  }
};

export const PersonalTrackingTab: React.FC<PersonalTrackingTabProps> = ({
  user,
  projects,
  locations
}) => {
  const [currentWorkEntry, setCurrentWorkEntry] = useState<ApiWorkEntry | null>(null);
  const [userStatus, setUserStatus] = useState<{
    isWorking: boolean;
    onBreak: boolean;
    currentWorkEntryId?: number;
    currentBreakId?: number;
  }>({
    isWorking: false,
    onBreak: false
  });
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [workComments, setWorkComments] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showMaterialDialog, setShowMaterialDialog] = useState(false);
  const [workStats, setWorkStats] = useState<WorkStats>({
    todayHours: 0,
    weekHours: 0,
    monthHours: 0,
    activeSessions: 0
  });
  const [recentEntries, setRecentEntries] = useState<ApiWorkEntry[]>([]);
  const [recentOperation, setRecentOperation] = useState<PersonalOperation | null>(null);
  const recentOperationRef = useRef<PersonalOperation | null>(null);
  const { toast } = useToast();
  
  // Use context safely without throwing
  const realTimeContext = useContext(RealTimeContext);
  const subscribeToWorkStarted = realTimeContext?.subscribeToWorkStarted || (() => () => {});
  const subscribeToWorkEnded = realTimeContext?.subscribeToWorkEnded || (() => () => {});
  const subscribeToTeamMemberStatusChanged = realTimeContext?.subscribeToTeamMemberStatusChanged || (() => () => {});
  
  // Initialize recent operation from global storage and sync with state
  useEffect(() => {
    // Clean up old operations first
    cleanupPersonalOperations();
    
    // Load operation for this user from global storage
    const userOperation = globalPersonalOperations.get(user.id);
    if (userOperation) {
      console.log(`Restored recent operation from global storage for user ${user.id}`);
      setRecentOperation(userOperation);
    }
  }, [user.id]);
  
  // Keep ref in sync with state and global storage
  useEffect(() => {
    recentOperationRef.current = recentOperation;
    
    // Update global storage
    if (recentOperation) {
      globalPersonalOperations.set(user.id, recentOperation);
    } else {
      globalPersonalOperations.delete(user.id);
    }
  }, [recentOperation, user.id]);

  useEffect(() => {
    loadPersonalData();
    
    // Listen for refresh events
    const handleRefresh = () => {
      // Check if we have recent operations before refreshing
      const recentOp = globalPersonalOperations.get(user.id) || recentOperationRef.current;
      if (recentOp && Date.now() - recentOp.timestamp < 60000) {
        console.log('Blocking refresh - recent operation in progress');
        return;
      }
      loadPersonalData();
    };
    window.addEventListener('supervisor-refresh', handleRefresh);
    
    // Handle tab visibility changes
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('PersonalTrackingTab became visible - checking for protected operations');
        
        const recentOp = globalPersonalOperations.get(user.id) || recentOperationRef.current;
        if (recentOp && Date.now() - recentOp.timestamp < 60000) {
          console.log('Blocking visibility refresh - recent operation in progress');
          return;
        }
        
        // Small delay to avoid rapid fire
        setTimeout(() => {
          console.log('Tab visibility refresh for PersonalTrackingTab');
          loadPersonalData();
        }, 500);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Auto refresh every 60 seconds (reduced frequency)
    const interval = setInterval(() => {
      const recentOp = globalPersonalOperations.get(user.id) || recentOperationRef.current;
      if (recentOp && Date.now() - recentOp.timestamp < 60000) {
        console.log('Skipping auto-refresh - recent operation in progress');
        return;
      }
      loadPersonalData();
    }, 60000);
    
    return () => {
      window.removeEventListener('supervisor-refresh', handleRefresh);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, [user.id]);
  
  // Debounce loadPersonalData to avoid too many calls
  const loadPersonalDataDebounced = React.useMemo(
    () => {
      let timeoutId: NodeJS.Timeout;
      return () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          loadPersonalData();
        }, 500);
      };
    },
    [user.id]
  );

  // Subscribe to real-time status updates
  useEffect(() => {
    if (!realTimeContext || !realTimeContext.state.isConnected) {
      console.warn('RealTime context not available or not connected');
      return;
    }
    
    console.log('Setting up SignalR subscriptions for PersonalTrackingTab');
    
    // Handle team member status changes (includes self)
    const unsubscribeStatus = subscribeToTeamMemberStatusChanged((event: any) => {
      console.log('PersonalTrackingTab: TeamMemberStatusChanged event received', event);
      
      // Check if this event is for the current user
      const userId = event.userId;
      if (userId === user.id) {
        console.log('Updating personal status from SignalR:', event);
        
        // Check if we have a recent operation that shouldn't be overridden
        const recentOp = globalPersonalOperations.get(user.id) || recentOperationRef.current;
        if (recentOp) {
          const timeSinceOp = Date.now() - recentOp.timestamp;
          if (timeSinceOp < 60000) {
            console.log(`Ignoring SignalR update - recent ${recentOp.operation} operation in progress (${timeSinceOp}ms ago)`);
            return; // Don't update if we have a recent operation
          }
        }
        
        // Update user status
        setUserStatus({
          isWorking: event.isWorking ?? false,
          onBreak: event.onBreak ?? false,
          currentWorkEntryId: event.currentWorkEntryId,
          currentBreakId: event.currentBreakId
        });
        
        // Update current work entry if provided
        if (event.currentWorkEntry) {
          setCurrentWorkEntry(event.currentWorkEntry);
        } else if (!event.isWorking) {
          setCurrentWorkEntry(null);
        }
        
        // Reload stats
        loadPersonalData();
      }
    });
    
    // Handle work started events
    const unsubscribeStarted = subscribeToWorkStarted((event: any) => {
      if (event.userId === user.id) {
        console.log('PersonalTrackingTab: Work started for self', event);
        
        // Check if we should delay processing this event to avoid race conditions
        const recentOp = globalPersonalOperations.get(user.id) || recentOperationRef.current;
        if (recentOp && recentOp.operation === 'start') {
          const timeSinceOp = Date.now() - recentOp.timestamp;
          if (timeSinceOp < 5000) {
            console.log(`Delaying SignalR WorkStarted processing - recent start operation (${timeSinceOp}ms ago)`);
            setTimeout(() => {
              console.log(`Processing delayed WorkStarted event`);
              setRecentOperation(null);
              setUserStatus(prev => ({
                ...prev,
                isWorking: true,
                onBreak: false,
                currentWorkEntryId: event.workEntry?.id
              }));
              if (event.workEntry) {
                setCurrentWorkEntry(event.workEntry);
              }
              loadPersonalData();
            }, 5000 - timeSinceOp);
            return;
          }
        }
        
        setUserStatus(prev => ({
          ...prev,
          isWorking: true,
          onBreak: false,
          currentWorkEntryId: event.workEntry?.id
        }));
        if (event.workEntry) {
          setCurrentWorkEntry(event.workEntry);
        }
        loadPersonalData();
      }
    });
    
    // Handle work ended events
    const unsubscribeEnded = subscribeToWorkEnded((event: any) => {
      if (event.userId === user.id) {
        console.log('PersonalTrackingTab: Work ended for self', event);
        
        // Check if we should delay processing this event to avoid race conditions
        const recentOp = globalPersonalOperations.get(user.id) || recentOperationRef.current;
        if (recentOp && recentOp.operation === 'stop') {
          const timeSinceOp = Date.now() - recentOp.timestamp;
          if (timeSinceOp < 5000) {
            console.log(`Delaying SignalR WorkEnded processing - recent stop operation (${timeSinceOp}ms ago)`);
            setTimeout(() => {
              console.log(`Processing delayed WorkEnded event`);
              // Re-check if operation is still protected
              const currentOp = globalPersonalOperations.get(user.id) || recentOperationRef.current;
              if (!currentOp || currentOp.operation !== 'stop' || Date.now() - currentOp.timestamp > 60000) {
                setRecentOperation(null);
                setUserStatus(prev => ({
                  ...prev,
                  isWorking: false,
                  onBreak: false,
                  currentWorkEntryId: undefined,
                  currentBreakId: undefined
                }));
                setCurrentWorkEntry(null);
                loadPersonalData();
              }
            }, 5000 - timeSinceOp);
            return;
          }
        }
        
        setUserStatus(prev => ({
          ...prev,
          isWorking: false,
          onBreak: false,
          currentWorkEntryId: undefined,
          currentBreakId: undefined
        }));
        setCurrentWorkEntry(null);
        loadPersonalData();
      }
    });
    
    return () => {
      // Safely call unsubscribe functions
      if (typeof unsubscribeStatus === 'function') {
        unsubscribeStatus();
      }
      if (typeof unsubscribeStarted === 'function') {
        unsubscribeStarted();
      }
      if (typeof unsubscribeEnded === 'function') {
        unsubscribeEnded();
      }
    };
  }, [realTimeContext, realTimeContext?.state.isConnected, user.id, subscribeToTeamMemberStatusChanged, subscribeToWorkStarted, subscribeToWorkEnded]);

  const loadPersonalData = async () => {
    try {
      setIsLoading(true);
      
      // Check if we have a recent operation that should override the status
      const recentOp = globalPersonalOperations.get(user.id) || recentOperationRef.current;
      if (recentOp) {
        const timeSinceOp = Date.now() - recentOp.timestamp;
        // If operation is less than 60 seconds old, handle it specially
        if (timeSinceOp < 60000) {
          console.log(`Personal tracking has recent ${recentOp.operation} operation (${timeSinceOp}ms ago), handling protection`);
          
          if (recentOp.operation === 'start') {
            // For recent start operations, force working status
            console.log('FORCING working status due to recent start operation');
            setUserStatus({
              isWorking: true,
              onBreak: false,
              currentWorkEntryId: recentOp.timestamp, // Temporary ID
              currentBreakId: undefined
            });
            // Don't load from API during protection window
            setIsLoading(false);
            
            // Schedule automatic refresh after protection window
            setTimeout(() => {
              console.log('Auto-refresh after start operation protection window');
              setRecentOperation(null);
              loadPersonalData();
            }, 62000 - timeSinceOp);
            return;
          } else if (recentOp.operation === 'stop') {
            // For recent stop operations, force inactive status
            console.log('FORCING inactive status due to recent stop operation');
            setUserStatus({
              isWorking: false,
              onBreak: false,
              currentWorkEntryId: undefined,
              currentBreakId: undefined
            });
            setCurrentWorkEntry(null);
            setIsLoading(false);
            
            // Schedule automatic refresh after protection window
            setTimeout(() => {
              console.log('Auto-refresh after stop operation protection window');
              setRecentOperation(null);
              loadPersonalData();
            }, 62000 - timeSinceOp);
            return;
          }
        } else {
          // Operation is older than 60 seconds, remove it
          console.log(`Removing old ${recentOp.operation} operation (${timeSinceOp}ms ago)`);
          setRecentOperation(null);
          globalPersonalOperations.delete(user.id);
        }
      }
      
      // Get user's current status from API
      console.log('Loading personal data for user:', user.id);
      try {
        const currentUser = await apiService.getUser(user.id);
        console.log('Current user status:', {
          isWorking: currentUser.isWorking,
          onBreak: currentUser.onBreak,
          currentWorkEntryId: currentUser.currentWorkEntryId,
          currentBreakId: currentUser.currentBreakId
        });
        
        // Update user status
        setUserStatus({
          isWorking: currentUser.isWorking || false,
          onBreak: currentUser.onBreak || false,
          currentWorkEntryId: currentUser.currentWorkEntryId,
          currentBreakId: currentUser.currentBreakId
        });
        
        // Always check for active work entry, regardless of user.isWorking status
        // This ensures we catch cases where the user status is out of sync
        try {
          const activeEntry = await apiService.getActiveWorkEntry(user.id);
          if (activeEntry && activeEntry.id) {
            console.log('Active work entry found:', activeEntry);
            setCurrentWorkEntry(activeEntry);
            
            // If we found an active work entry, ensure user status reflects this
            setUserStatus(prev => ({
              ...prev,
              isWorking: true,
              currentWorkEntryId: activeEntry.id,
              // Keep other properties from API if they were set
              onBreak: currentUser.onBreak || prev.onBreak,
              currentBreakId: currentUser.currentBreakId || prev.currentBreakId
            }));
            
            console.log('User status updated to reflect active work entry');
          } else {
            console.log('No active work entry found');
            setCurrentWorkEntry(null);
            
            // Only update to not working if user status from API also says not working
            if (!currentUser.isWorking) {
              setUserStatus(prev => ({
                ...prev,
                isWorking: false,
                currentWorkEntryId: undefined,
                currentBreakId: undefined
              }));
            }
          }
        } catch (error) {
          console.error('Error fetching active work entry:', error);
          setCurrentWorkEntry(null);
          
          // Fall back to user status from API
          // Only clear work status if API also says user is not working
          if (!currentUser.isWorking) {
            setUserStatus(prev => ({
              ...prev,
              isWorking: false,
              currentWorkEntryId: undefined
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching user status, using fallback:', error);
        // Fallback to old method if user endpoint is not available
        const activeEntry = await apiService.getActiveWorkEntry(user.id);
        console.log('Active work entry (fallback):', activeEntry);
        
        if (activeEntry && activeEntry.id) {
          setCurrentWorkEntry(activeEntry);
          setUserStatus({
            isWorking: true,
            onBreak: false,
            currentWorkEntryId: activeEntry.id
          });
        } else {
          setCurrentWorkEntry(null);
          setUserStatus({
            isWorking: false,
            onBreak: false
          });
        }
      }
      
      // Get work entries for stats
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      startOfMonth.setHours(0, 0, 0, 0);
      
      // Fetch work entries more efficiently
      // The API returns all entries when no date is specified, filtered by userId
      const allEntries: ApiWorkEntry[] = [];
      
      try {
        console.log(`Fetching all work entries for user ${user.id}`);
        
        // Try to get all entries for the user (API should handle filtering)
        const entriesResponse = await apiService.getWorkEntries(undefined, user.id);
        const fetchedEntries = Array.isArray(entriesResponse) 
          ? entriesResponse 
          : (entriesResponse.data || []);
        
        // Filter to only include entries from this month
        const thisMonthEntries = fetchedEntries.filter(entry => {
          const entryDate = new Date(entry.date);
          return entryDate >= startOfMonth && entryDate <= today;
        });
        
        allEntries.push(...thisMonthEntries);
        
        // If we got no entries or the API doesn't support fetching all, fall back to fetching today
        if (allEntries.length === 0) {
          console.log('No entries found, fetching today\'s entries as fallback');
          const todayEntries = await apiService.getWorkEntries(
            today.toISOString().split('T')[0],
            user.id
          );
          const entries = Array.isArray(todayEntries) 
            ? todayEntries 
            : (todayEntries.data || []);
          allEntries.push(...entries);
        }
      } catch (error) {
        console.error('Error fetching work entries:', error);
      }
      
      console.log(`Fetched ${allEntries.length} total work entries`);
      
      // Calculate stats
      let todayHours = 0;
      let weekHours = 0;
      let monthHours = 0;
      let activeSessions = 0;
      
      // Count active sessions (should be 0 or 1)
      // Use the component state which is updated from API
      if (userStatus.isWorking || currentWorkEntry) {
        activeSessions = 1;
      }
      
      allEntries.forEach(entry => {
        const entryDate = new Date(entry.date);
        entryDate.setHours(0, 0, 0, 0);
        const todayStart = new Date(today);
        todayStart.setHours(0, 0, 0, 0);
        
        const hours = calculateHours(entry);
        
        // Month hours (all entries)
        monthHours += hours;
        
        // Week hours
        if (entryDate >= startOfWeek) {
          weekHours += hours;
        }
        
        // Today hours
        if (entryDate.getTime() === todayStart.getTime()) {
          todayHours += hours;
        }
      });
      
      setWorkStats({
        todayHours,
        weekHours,
        monthHours,
        activeSessions
      });
      
      // Get recent entries (last 5)
      const sortedEntries = allEntries
        .sort((a, b) => {
          const dateA = new Date(a.startTime || a.date).getTime();
          const dateB = new Date(b.startTime || b.date).getTime();
          return dateB - dateA;
        })
        .slice(0, 5);
      setRecentEntries(sortedEntries);
      
    } catch (error) {
      console.error('Error loading personal data:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele personale',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const calculateHours = (entry: ApiWorkEntry): number => {
    if (!entry.startTime) return 0;
    const start = new Date(entry.startTime);
    const end = entry.endTime ? new Date(entry.endTime) : new Date();
    const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    const breakMinutes = entry.totalBreakMinutes || entry.breakMinutes || 0;
    return Math.max(0, hours - breakMinutes / 60);
  };

  const handleStartWork = async () => {
    if (!selectedProject || !selectedLocation) {
      toast({
        title: 'Atenție',
        description: 'Selectați un proiect și o locație',
        variant: 'destructive'
      });
      return;
    }

    try {
      setIsLoading(true);
      
      // Get project and location names for the new entry
      const selectedProjectObj = projects.find(p => p.id === parseInt(selectedProject));
      const selectedLocationObj = locations.find(l => l.id === parseInt(selectedLocation));
      
      console.log('Starting work with:', {
        projectId: selectedProject,
        projectName: selectedProjectObj?.name,
        locationId: selectedLocation,
        locationName: selectedLocationObj?.name
      });
      
      // Track this operation
      const operation: PersonalOperation = {
        operation: 'start',
        timestamp: Date.now(),
        projectId: parseInt(selectedProject),
        locationId: parseInt(selectedLocation)
      };
      setRecentOperation(operation);
      
      const newEntry = await apiService.createWorkEntry({
        userId: user.id,
        projectId: parseInt(selectedProject),
        locationId: parseInt(selectedLocation),
        startTime: new Date().toISOString(),
        date: new Date().toISOString().split('T')[0]
      });
      
      // Ensure the entry has project and location names
      if (newEntry) {
        newEntry.projectName = selectedProjectObj?.name || 'Proiect nedefinit';
        newEntry.locationName = selectedLocationObj?.name || 'Locație nedefinită';
        console.log('New work entry created:', newEntry);
        
        // Validate that the entry has an ID
        if (!newEntry.id) {
          console.error('New work entry does not have an ID:', newEntry);
          throw new Error('Work entry created without ID');
        }
      }
      
      setCurrentWorkEntry(newEntry);
      setSelectedProject('');
      setSelectedLocation('');
      
      // Update user status immediately
      setUserStatus({
        isWorking: true,
        onBreak: false,
        currentWorkEntryId: newEntry.id,
        currentBreakId: undefined
      });
      
      toast({
        title: 'Succes',
        description: 'Ați început munca',
      });
      
      // Schedule automatic refresh after protection window expires
      setTimeout(() => {
        console.log('Auto-refresh after start operation protection window');
        setRecentOperation(null);
        loadPersonalData();
      }, 62000);
      
      // Reload data after a short delay to ensure backend is updated
      setTimeout(() => loadPersonalData(), 1000);
    } catch (error) {
      console.error('Error starting work:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut începe munca',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStopWork = async () => {
    if (!currentWorkEntry) {
      console.error('No current work entry to stop');
      toast({
        title: 'Eroare',
        description: 'Nu există o sesiune de lucru activă',
        variant: 'destructive'
      });
      return;
    }
    
    if (!currentWorkEntry.id) {
      console.error('Current work entry has no ID:', currentWorkEntry);
      toast({
        title: 'Eroare',
        description: 'Sesiunea de lucru nu are un ID valid. Vă rugăm să reîncărcați pagina.',
        variant: 'destructive'
      });
      return;
    }

    try {
      setIsLoading(true);
      console.log('Stopping work entry:', currentWorkEntry.id, 'with comments:', workComments);
      
      // Track this operation
      const operation: PersonalOperation = {
        operation: 'stop',
        timestamp: Date.now()
      };
      setRecentOperation(operation);
      
      // Use the bulk stop work API for consistency
      const result = await apiService.bulkStopWork(
        [currentWorkEntry.id],
        workComments || 'Oprit de supervizor'
      );
      
      if (result.successCount === 0) {
        throw new Error('Failed to stop work entry');
      }
      
      setCurrentWorkEntry(null);
      setWorkComments('');
      
      // Update user status immediately
      setUserStatus({
        isWorking: false,
        onBreak: false,
        currentWorkEntryId: undefined,
        currentBreakId: undefined
      });
      
      toast({
        title: 'Succes',
        description: 'Ați oprit munca',
      });
      
      // Schedule automatic refresh after protection window expires
      setTimeout(() => {
        console.log('Auto-refresh after stop operation protection window');
        setRecentOperation(null);
        loadPersonalData();
      }, 62000);
      
      // Reload data after a short delay to ensure backend is updated
      setTimeout(() => loadPersonalData(), 1000);
    } catch (error) {
      console.error('Error stopping work:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut opri munca',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartBreak = async () => {
    if (!currentWorkEntry) {
      console.error('No current work entry for break');
      return;
    }
    
    if (!currentWorkEntry.id) {
      console.error('Current work entry has no ID for break:', currentWorkEntry);
      toast({
        title: 'Eroare',
        description: 'Sesiunea de lucru nu are un ID valid pentru pauză.',
        variant: 'destructive'
      });
      return;
    }

    try {
      setIsLoading(true);
      const newBreak = await apiService.createBreak({
        workEntryId: currentWorkEntry.id,
        startTime: new Date().toISOString(),
        reason: 'Pauză'
      });
      
      // Update user status immediately
      setUserStatus(prev => ({
        ...prev,
        onBreak: true,
        currentBreakId: newBreak?.id
      }));
      
      // Update current work entry with active break
      if (currentWorkEntry) {
        setCurrentWorkEntry({
          ...currentWorkEntry,
          activeBreak: newBreak
        });
      }
      
      toast({
        title: 'Succes',
        description: 'Ați început pauza',
      });
      
      // Reload data after a short delay to ensure backend is updated
      setTimeout(() => loadPersonalData(), 1000);
    } catch (error) {
      console.error('Error starting break:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut începe pauza',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndBreak = async () => {
    if (!currentWorkEntry?.activeBreak) {
      console.error('No active break to end');
      return;
    }
    
    if (!currentWorkEntry.activeBreak.id) {
      console.error('Active break has no ID:', currentWorkEntry.activeBreak);
      toast({
        title: 'Eroare',
        description: 'Pauza nu are un ID valid.',
        variant: 'destructive'
      });
      return;
    }

    try {
      setIsLoading(true);
      await apiService.updateBreak(currentWorkEntry.activeBreak.id, {
        endTime: new Date().toISOString()
      });
      
      // Update user status immediately
      setUserStatus(prev => ({
        ...prev,
        onBreak: false,
        currentBreakId: undefined
      }));
      
      // Update current work entry to remove active break
      if (currentWorkEntry) {
        setCurrentWorkEntry({
          ...currentWorkEntry,
          activeBreak: undefined
        });
      }
      
      toast({
        title: 'Succes',
        description: 'Ați terminat pauza',
      });
      
      // Reload data after a short delay to ensure backend is updated
      setTimeout(() => loadPersonalData(), 1000);
    } catch (error) {
      console.error('Error ending break:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut termina pauza',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDuration = (hours: number): string => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const formatTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid time';
      }
      return date.toLocaleTimeString('ro-RO', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid time';
    }
  };

  return (
    <div className="space-y-6">
      {/* Work Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <Clock className="h-8 w-8 text-blue-500 mb-2" />
              <p className="text-sm text-gray-600">Azi</p>
              <p className="text-2xl font-bold">{formatDuration(workStats.todayHours)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <Calendar className="h-8 w-8 text-green-500 mb-2" />
              <p className="text-sm text-gray-600">Săptămâna</p>
              <p className="text-2xl font-bold">{formatDuration(workStats.weekHours)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <TrendingUp className="h-8 w-8 text-purple-500 mb-2" />
              <p className="text-sm text-gray-600">Luna</p>
              <p className="text-2xl font-bold">{formatDuration(workStats.monthHours)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <Timer className="h-8 w-8 text-orange-500 mb-2" />
              <p className="text-sm text-gray-600">Sesiuni Active</p>
              <p className="text-2xl font-bold">{workStats.activeSessions}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Current Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Status Curent</span>
            {(() => {
              // Check for recent operation
              const recentOp = globalPersonalOperations.get(user.id) || recentOperation;
              if (recentOp) {
                const timeSinceOp = Date.now() - recentOp.timestamp;
                if (timeSinceOp < 60000) {
                  const isStartOperation = recentOp.operation === 'start';
                  return (
                    <Badge variant="warning" className="text-lg px-3 py-1 animate-pulse">
                      {isStartOperation ? 'Se procesează pornirea...' : 'Se procesează oprirea...'}
                    </Badge>
                  );
                }
              }
              
              // Normal status display
              return userStatus.isWorking ? (
                userStatus.onBreak ? (
                  <Badge variant="warning" className="text-lg px-3 py-1">
                    <Coffee className="h-4 w-4 mr-1" />
                    În Pauză
                  </Badge>
                ) : (
                  <Badge variant="success" className="text-lg px-3 py-1">
                    <Play className="h-4 w-4 mr-1" />
                    La Lucru
                  </Badge>
                )
              ) : (
                <Badge variant="secondary" className="text-lg px-3 py-1">
                  <Square className="h-4 w-4 mr-1" />
                  Inactiv
                </Badge>
              );
            })()}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {userStatus.isWorking && currentWorkEntry ? (
            <>
              {/* Active Work Info */}
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                      <Briefcase className="h-4 w-4" />
                      Proiect
                    </p>
                    <p className="font-medium">{currentWorkEntry.projectName || 'Proiect nedefinit'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      Locație
                    </p>
                    <p className="font-medium">{currentWorkEntry.locationName || 'Locație nedefinită'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      Început la
                    </p>
                    <p className="font-medium">{currentWorkEntry.startTime ? formatTime(currentWorkEntry.startTime) : 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Active Work Actions */}
              <div className="space-y-3">
                <div>
                  <Label>Comentarii muncă (opțional)</Label>
                  <Textarea
                    value={workComments}
                    onChange={(e) => setWorkComments(e.target.value)}
                    placeholder="Descrieți activitățile efectuate..."
                    rows={3}
                  />
                </div>

                <div className="flex gap-2">
                  {userStatus.onBreak ? (
                    <Button
                      onClick={handleEndBreak}
                      disabled={isLoading}
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      <Play className="h-4 w-4" />
                      Termină Pauza
                    </Button>
                  ) : (
                    <Button
                      onClick={handleStartBreak}
                      disabled={isLoading}
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      <Coffee className="h-4 w-4" />
                      Începe Pauză
                    </Button>
                  )}

                  <Button
                    onClick={() => setShowMaterialDialog(true)}
                    disabled={isLoading}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Package className="h-4 w-4" />
                    Adaugă Material
                  </Button>

                  <Button
                    onClick={handleStopWork}
                    disabled={isLoading}
                    variant="destructive"
                    className="flex items-center gap-2 ml-auto"
                  >
                    <Square className="h-4 w-4" />
                    Oprește Munca
                  </Button>
                </div>
              </div>

              {userStatus.onBreak && currentWorkEntry.activeBreak && (
                <Alert>
                  <Coffee className="h-4 w-4" />
                  <AlertDescription>
                    Sunteți în pauză din {formatTime(currentWorkEntry.activeBreak.startTime)}
                  </AlertDescription>
                </Alert>
              )}
            </>
          ) : (
            <>
              {/* Start Work Form */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Proiect</Label>
                  <Select value={selectedProject} onValueChange={setSelectedProject}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selectează proiect" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects && projects.length > 0 ? (
                        projects.map(project => (
                          <SelectItem key={project.id} value={project.id.toString()}>
                            {project.name}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-2 text-sm text-gray-500">Nu există proiecte disponibile</div>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Locație</Label>
                  <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selectează locație" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations && locations.length > 0 ? (
                        locations.map(location => (
                          <SelectItem key={location.id} value={location.id.toString()}>
                            {location.name}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-2 text-sm text-gray-500">Nu există locații disponibile</div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button
                onClick={handleStartWork}
                disabled={isLoading || !selectedProject || !selectedLocation}
                className="w-full flex items-center justify-center gap-2"
                size="lg"
              >
                <Play className="h-5 w-5" />
                Începe Munca
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Recent Entries */}
      <Card>
        <CardHeader>
          <CardTitle>Activitate Recentă</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {recentEntries.map(entry => (
              <div key={entry.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">{entry.projectName || 'Proiect nedefinit'} - {entry.locationName || 'Locație nedefinită'}</p>
                  <p className="text-sm text-gray-600">
                    {new Date(entry.date).toLocaleDateString('ro-RO')} • 
                    {entry.startTime && ` ${formatTime(entry.startTime)}`}
                    {entry.endTime && ` - ${formatTime(entry.endTime)}`}
                    {entry.comments && ` • ${entry.comments}`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-medium">{formatDuration(calculateHours(entry))}</p>
                  {entry.isActive && <Badge variant="success">Activ</Badge>}
                </div>
              </div>
            ))}

            {recentEntries.length === 0 && (
              <p className="text-center text-gray-500 py-4">
                Nu există activitate recentă
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Material Consumption for Current Entry */}
      {currentWorkEntry && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Materiale Consumate - Sesiune Curentă
            </CardTitle>
          </CardHeader>
          <CardContent>
            <MaterialConsumptionList
              workEntryId={currentWorkEntry.id}
              showActions={true}
              compact={false}
            />
          </CardContent>
        </Card>
      )}

      {/* Material Dialog */}
      {showMaterialDialog && currentWorkEntry && (
        <Dialog open={showMaterialDialog} onOpenChange={setShowMaterialDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Adaugă Consum Material Personal</DialogTitle>
            </DialogHeader>
            
            <MaterialConsumptionForm
              workEntryId={currentWorkEntry.id}
              addedByUserId={user.id}
              addedForUserId={user.id}
              onSuccess={() => {
                setShowMaterialDialog(false);
                toast({
                  title: 'Succes',
                  description: 'Material adăugat cu succes',
                });
              }}
              onCancel={() => setShowMaterialDialog(false)}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};